import React, { useState, useEffect, useRef } from 'react';
import { debugLog, debugError } from '../utils/debug';
import { useUser } from '@clerk/clerk-react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import * as apiUtils from './ChatInterface/apiUtils';
import { getApiUrl } from '../utils/apiUtils';
import { useChatManager } from './ChatManager';

const ChatModal = ({ chatId, isOpen, isMinimized, onClose, explanationData }) => {
  const { user } = useUser();
  const { minimizeChat, updateChatState, chats } = useChatManager();
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  
  // Get chat state from ChatManager
  const chat = chats.find(c => c.id === chatId);
  const messages = chat?.messages || [];
  const isLoading = chat?.isLoading || false;
  const conversationId = chat?.conversationId || null;

  const scrollToBottom = () => {
    // Try multiple approaches to ensure scrolling works
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
    
    // Also try scrolling the messages container directly
    const messagesContainer = document.querySelector('.chat-messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Scroll to bottom when modal opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [isOpen]);

  // Clear input when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewMessage('');
    }
  }, [isOpen]);

  // Handle Escape key to close chat modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation(); // Prevent other handlers from running
        onClose();
      }
    };

    if (isOpen) {
      // Use capture phase to ensure this handler runs first
      document.addEventListener('keydown', handleEscapeKey, true);
      return () => document.removeEventListener('keydown', handleEscapeKey, true);
    }
  }, [isOpen, onClose]);

  // Clear chat state only when it's truly a new instruction
  useEffect(() => {
    if (explanationData?.instruction && chat && messages.length === 0 && !isLoading) {
      updateChatState(chatId, {
        messages: [],
        conversationId: null,
        isLoading: false
      });
      setNewMessage('');
    }
  }, [explanationData?.instruction, chatId]); // Simplified dependencies


  const sendMessage = async (messageContentOverride = null) => {
    const messageContent = messageContentOverride || newMessage.trim();
    if (!messageContent || isLoading) {
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString(),
    };

    debugLog('ChatModal: Adding user message:', userMessage);
    debugLog('ChatModal: Current messages before adding user:', messages);
    updateChatState(chatId, (prevChat) => ({
      messages: [...(prevChat.messages || []), userMessage],
      isLoading: true
    }));
    setNewMessage('');

    try {
      let currentConversationId = conversationId;

      // Create conversation if it doesn't exist
      if (!currentConversationId) {
        const API_URL = getApiUrl(); // Get at runtime
        const convResponse = await fetch(`${API_URL}/messages/conversations`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await apiUtils.getToken(user)}`,
            'Content-Type': 'application/json',
          },
        });

        if (!convResponse.ok) {
          throw new Error('Failed to create conversation');
        }

        const conversation = await convResponse.json();
        currentConversationId = conversation.id;
        updateChatState(chatId, { conversationId: currentConversationId });
      }

      // Check if this is an explanation request and use the special endpoint
      // If we have explanationData (Help Mode), always use the fast help mode endpoint
      const isExplanationRequest = explanationData && true;
      
      if (isExplanationRequest) {
        const API_URL = getApiUrl(); // Get at runtime
        const explainResponse = await fetch(`${API_URL}/api/help-mode/explain-instruction`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await apiUtils.getToken(user)}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            instruction: messageContent,  // Use user's question instead of original instruction
            workout_id: explanationData.workoutId,
            workout_context: explanationData.workoutContext,
            workout_title: explanationData.title,
            workout_subtitle: explanationData.subtitle,
          }),
        });

        if (!explainResponse.ok) {
          throw new Error('Failed to get explanation');
        }

        const explainResult = await explainResponse.json();
              const aiMessage = {
                id: `ai-${Date.now()}`,
                role: 'assistant',
                content: explainResult.explanation,
                timestamp: new Date().toISOString(),
              };

              debugLog('ChatModal: Adding AI message (Help Mode):', aiMessage);
              debugLog('ChatModal: Current messages before adding AI:', messages);
              updateChatState(chatId, (prevChat) => ({
                messages: [...(prevChat.messages || []), aiMessage],
                isLoading: false
              }));

      } else {
        // Regular message - send to normal chat endpoint
        const API_URL = getApiUrl(); // Get at runtime
        const response = await fetch(`${API_URL}/messages/conversations/${currentConversationId}/messages`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await apiUtils.getToken(user)}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: messageContent,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to send message');
        }

              // For now, just add a placeholder response
              // In a full implementation, you'd handle streaming responses
              const aiMessage = {
                id: `ai-${Date.now()}`,
                role: 'assistant',
                content: 'Thank you for your question! I\'m processing your request.',
                timestamp: new Date().toISOString(),
              };

              updateChatState(chatId, (prevChat) => ({
                messages: [...(prevChat.messages || []), aiMessage],
                isLoading: false
              }));
      }

      // Scroll to bottom after AI response is added
      setTimeout(() => {
        scrollToBottom();
      }, 200);

    } catch (error) {
      debugError('Error sending message:', error);
      const errorMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I had trouble processing your message. Please try again.',
        timestamp: new Date().toISOString(),
      };
      updateChatState(chatId, (prevChat) => ({
        messages: [...(prevChat.messages || []), errorMessage],
        isLoading: false
      }));

      // Scroll to bottom after error message is added
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      {/* Maximized modal with backdrop */}
      {!isMinimized && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-2 sm:p-4">
          <div className="bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl rounded-xl w-full max-w-none sm:max-w-2xl max-h-[90vh] overflow-hidden shadow-xl border border-gray-700/50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700/50 flex-shrink-0">
          <div className="flex-1 min-w-0 mr-4">
            <h2 className="text-lg font-semibold text-white truncate">Workout Help</h2>
            <p className="text-sm text-gray-400 truncate">Ask questions about your workout</p>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            <button
              onClick={() => minimizeChat(chatId)}
              className="text-gray-400 hover:text-white transition-colors p-1"
              title="Minimize"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-1"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        {(
          <div 
            className="chat-messages-container flex-1 overflow-y-auto p-4 space-y-4 min-h-0" 
            style={{ 
              maxHeight: 'calc(90vh - 200px)',
              scrollbarWidth: 'thin',
              scrollbarColor: '#4B5563 #1F2937'
            }}
          >
          {/* Question Preview Card - Modern Design */}
          {messages.length === 0 && explanationData && (
            <div className="space-y-3">
              {/* Question Preview */}
              <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-400/20 rounded-xl p-4">
                <div className="flex items-start space-x-3 w-full">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0 w-full">
                    <h3 className="text-sm font-medium text-blue-300 mb-2">Ready to ask about:</h3>
                    <div className="text-sm text-gray-200 leading-relaxed">
                      <ReactMarkdown 
                        components={{
                          p: ({ children }) => <span>{children}</span>,
                          strong: ({ children }) => <strong className="font-semibold text-blue-300">{children}</strong>,
                          em: ({ children }) => <em className="italic text-blue-200">{children}</em>
                        }}
                      >
                        {typeof explanationData.instruction === 'string' ? explanationData.instruction : String(explanationData.instruction || '')}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Helper Text */}
              <div className="text-center">
                <p className="text-xs text-gray-400">
                  Click "Use Suggested Question" to send immediately, or "Edit Question" to customize
                </p>
              </div>
            </div>
          )}
          
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[95%] px-4 py-2 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-700 text-gray-100'
                }`}
              >
                {message.role === 'user' ? (
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap">{children}</p>,
                      strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                      em: ({ children }) => <em className="italic">{children}</em>
                    }}
                  >
                    {typeof message.content === 'string' ? message.content : String(message.content || '')}
                  </ReactMarkdown>
                ) : (
                  <div className="prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                        strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                        em: ({ children }) => <em className="italic">{children}</em>,
                        ul: ({ children }) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                        h1: ({ children }) => <h1 className="text-lg font-bold mb-2 text-white">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-base font-bold mb-2 text-white">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-sm font-bold mb-1 text-white">{children}</h3>,
                      }}
                    >
                      {typeof message.content === 'string' ? message.content : String(message.content || '')}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-700 text-gray-100 px-4 py-2 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>AI Coach is thinking...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
          </div>
        )}

        {/* Action Buttons - Full Width */}
        {messages.length === 0 && explanationData && (
          <div className="px-4 py-3 border-t border-gray-700/50 flex-shrink-0">
            <div className="flex space-x-3 w-full">
        <button
          onClick={() => {
            const question = `Can you explain this instruction from my workout: "${explanationData.instruction}"?`;
            setNewMessage(question);
            sendMessage(question);
          }}
          className="flex-1 px-4 py-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 text-sm font-medium rounded-lg border border-blue-400/30 transition-all duration-200 hover:border-blue-400/50 flex items-center justify-center space-x-2 min-w-0"
        >
          <span>📝</span>
          <span>Use Suggested Question</span>
        </button>
        <button
          onClick={() => {
            const editQuestion = `What does "${explanationData.instruction}" mean?`;
            setNewMessage(editQuestion);
            setTimeout(() => {
              const input = document.querySelector('input[placeholder="Type your question here..."]');
              if (input) input.focus();
            }, 100);
          }}
          className="flex-1 px-4 py-3 bg-gray-500/20 hover:bg-gray-500/30 text-gray-300 text-sm font-medium rounded-lg border border-gray-400/30 transition-all duration-200 hover:border-gray-400/50 flex items-center justify-center space-x-2 min-w-0"
        >
          <span>✏️</span>
          <span>Edit Question</span>
        </button>
            </div>
          </div>
        )}

        {/* Input */}
        {(
          <div className="p-4 border-t border-gray-700/50 flex-shrink-0">
            <div className="flex space-x-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Type your question here..."
                className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!newMessage.trim() || isLoading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Send
              </button>
            </div>
          </div>
        )}
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default ChatModal;
